import { useState } from "react";
import { ItemSummary, LayerItem } from "../../services/types";
import { formatDate, formatValueBase, parseMarkdown } from "../../utils/helpers";
import ItemModal from "./ItemModal";

interface ItemCardProps {
    width: string;
    layerItem: LayerItem;
    summary: ItemSummary | null;
    isActive: boolean;
    setActiveCardId: (id: number) => void;
};

const ItemCard = ({ width, layerItem, summary, isActive, setActiveCardId }: ItemCardProps) => {
    const [showDetails, setShowDetails] = useState(false);

    // Destructure metric properties
    const { description, name, value, category, activity_date, attributes, id } = layerItem;

    // Extract image_url from attributes
    const imageUrl = attributes.find((attr: any) => attr.name === "image_url")?.value;

    // Format activity date
    const formattedDate = formatDate(activity_date);

    // Handle click event for showing modal details
    const handleClick = () => {
        setActiveCardId(id); // Update the last active card

        setShowDetails((prev) => !prev);
    };

    return (
        <>
        <div
            className={`bg-white border border-[#E4E4E4] p-4 rounded-md shadow-md ${width} flex flex-row items-start gap-4 hover:cursor-pointer transition-all duration-150 
            ${isActive ? 'border-4 border-port-green shadow-lg' : ''} 
            hover:shadow-lg hover:border-2 hover:border-port-green`}
            onClick={handleClick}
        >
            {/* Image on the left */}
            {imageUrl && (
                <img
                    src={imageUrl}
                    alt={`${name} Image`}
                    className="w-32 object-cover"
                />
            )}

            {/* Content on the right */}
            <div className="flex flex-col justify-between flex-1 overflow-hidden">
                {/* Name */}
                <h2 className="text-lg font-semibold text-[#005073] mb-2">{name}</h2>

                {/* Description with HTML */}
                <div
                    className='text-xs text-gray-600 line-clamp-2'
                    dangerouslySetInnerHTML={{ __html: parseMarkdown(description) }}
                />
                
                <div className="text-xs text-blue-500 hover:text-blue-700 text-end mb-2">Show more</div>

                {/* Fund Amount */}
                {value ? 
                    <>
                        <p className="text-sm font-semibold text-gray-700">
                            Total: {formatValueBase(value, 'currency')}
                        </p>
                        <p className="text-xs text-gray-700"> {summary?.unit_descriptor}</p>
                    </>
                    :
                    null
                }

                {/* Footer with Category and Activity Date */}
                <div className="flex items-center justify-between mt-4 text-xs text-gray-500">
                    <span className="font-semibold">{category}</span>
                    <span>{formattedDate}</span>
                </div>
            </div>
        </div>

        <ItemModal isOpen={showDetails} item={layerItem} onClose={handleClick} summary={summary} />
        </>
    );
};

export default ItemCard;
